import { Injectable } from "@angular/core";
import { BaseLookupService } from "../../impl/BaseLookupService";
import { IHedgeOrderDataLookupService } from "../IHedgeOrderDataLookupService";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BmarkFixingData } from "shared/cubiq-services/model/benchmarks/BmarkFixingData.model";
import { BmarkFixingType } from "shared/cubiq-services/model/benchmarks/enums/BmarkFixingType.enum";
import { BmarkAllocationMetaData } from "shared/cubiq-services/model/benchmarks/BmarkAllocationMetaData.model";
import { ActiveUser } from "shared/cubiq-services/model/core/ActiveUser";
import { Serializer } from "shared/provider-webworker";
import { BmarkOrderData } from "../../../model/benchmarks/BmarkOrderData.model";
import { TraderGroupLookupService } from "./TraderGroupLookupService";
import { FlashingWaitTimeManager } from "./FlashingWaitTimeManager";
import { HEDGE_ORDER_REJECT_STATES } from "../../../../auth-services";

export const BLOTTERS_HEDGE_ORDERS: string[] = ["Hedge Orders"];

export const PRIMING_BATCH_SIZE: number = 100;

export const HEDGE_ORDER_NEW_ORDER_STATES: Set<CubiqTS.OrderState> = new Set([
  CubiqTS.OrderState.DI_NEEDED,
]);

export class HedgeOrderQueuedEvent {
  constructor(public event: any, public processEventFunction: Function) {}
}

const SOFT_CUTOFF = CubiqTS.FixingState.SOFT_CUTOFF;
const HARD_CUTOFF = CubiqTS.FixingState.HARD_CUTOFF;

@Injectable()
export class HedgeOrderDataLookupService
  extends BaseLookupService<string, CubiqTS.OrderDataImpl>
  implements IHedgeOrderDataLookupService
{
  private activeUser: ActiveUser;
  protected _serializer: Serializer = new CubiqTS.CubiqSerializer();
  protected readonly VALUE: CubiqTS.ConcreteEncodable | CubiqTS.ConcreteEncodable[];

  protected recentFixings: Map<string, BmarkFixingData> = new Map();
  protected activeFixings: Map<string, BmarkFixingData> = new Map();

  protected bmarkOrderAllocationMapping: Map<string, Set<string>> = new Map(); // orderId => [allocationIds...]
  protected bmarkAllocationMetaData: Map<string, BmarkAllocationMetaData> = new Map(); // allocationId => BmarkAllocationMetaData

  public flashManager: FlashingWaitTimeManager;

  constructor(protected traderGroupLookup: TraderGroupLookupService) {
    super(CubiqTS.OrderDataImpl);

    this.flashManager = new FlashingWaitTimeManager();

    this.VALUE = CubiqTS.ENCODABLE(CubiqTS.OrderDataImpl);
  }

  public setActiveUser(activeUser: ActiveUser): void {
    if (activeUser instanceof ActiveUser) {
      this.activeUser = activeUser;
    }
  }

  /* ******************************************************* */
  /* SUPER Overwrites  */

  public reset() {
    super.reset();

    this.recentFixings.clear();
    this.activeFixings.clear();

    this.bmarkOrderAllocationMapping.clear();
    this.bmarkAllocationMetaData.clear();
  }

  public deleteItem(item: CubiqTS.OrderDataImpl): void {
    super.deleteItem(item);
    this.flashManager.removeWaitHandler(item);
    this.deleteOrderAllocations(this.getItemKey(item));
  }

  public deleteItemById(id: string): void {
    let item = this.getItem(id);
    super.deleteItemById(id);
    this.deleteOrderAllocations(id);
  }

  public mapItem(item: CubiqTS.OrderDataImpl): void {
    item = this.preprocessOrderData(item);
    super.mapItem(item);
    this.flashManager.checkOrderForFlashHandle(item);
    this.processOrderForAllocations(item);
  }

  public mapItemById(item: CubiqTS.OrderDataImpl, id: string): void {
    item = this.preprocessOrderData(item);
    super.mapItemById(item, id);
    this.flashManager.checkOrderForFlashHandle(item);
    this.processOrderForAllocations(item);
  }

  public getItemKey(item: CubiqTS.OrderDataImpl): string {
    let order = item as CubiqTS.OrderDataImpl;
    return order.orderId.toString();
  }

  public hasItem(orderId: string): boolean {
    return super.getItem(orderId) != null;
  }

  /* ******************************************************* */
  /* ALLOCATIONs  */

  public orderHasAllocations(orderId: string): boolean {
    return this.bmarkOrderAllocationMapping.has(orderId);
  }

  public orderHasAllocationId(orderId: string, allocationId: string): boolean {
    return (
      this.bmarkOrderAllocationMapping.has(orderId) &&
      this.bmarkOrderAllocationMapping.get(orderId).has(allocationId)
    );
  }

  public getAllocationIdsForOrder(orderId: string): Array<string> {
    if (this.bmarkOrderAllocationMapping.has(orderId)) {
      Array.from(this.bmarkOrderAllocationMapping.get(orderId));
    }
    return null;
  }

  public allocationHasMetaData(allocationId: string): boolean {
    return this.bmarkAllocationMetaData.has(allocationId);
  }

  public getAllocationMetaData(allocationId: string): BmarkAllocationMetaData {
    if (this.allocationHasMetaData(allocationId)) {
      return this.bmarkAllocationMetaData.get(allocationId);
    }
    return null;
  }

  public getAllocationMetaDataForOrder(orderId: string): Array<BmarkAllocationMetaData> {
    if (this.orderHasAllocations(orderId)) {
      let result = Array<BmarkAllocationMetaData>();
      let allocationIds = this.bmarkOrderAllocationMapping.get(orderId);
      for (let allocationId of allocationIds) {
        let allocationMetaData = this.getAllocationMetaData(allocationId);
        if (allocationMetaData != null) {
          result.push(allocationMetaData);
        }
      }
      return result;
    }
    return null;
  }

  public deleteOrderAllocations(orderId: string): void {
    if (this.bmarkOrderAllocationMapping.has(orderId)) {
      let allocationIds = this.bmarkOrderAllocationMapping.get(orderId);
      for (let allocationId of allocationIds) {
        this.bmarkAllocationMetaData.delete(allocationId);
      }
      this.bmarkOrderAllocationMapping.delete(orderId);
    }
  }

  public processOrderForAllocations(orderData: CubiqTS.OrderDataImpl | BmarkOrderData): void {
    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      Array.isArray(orderData.allocationData) &&
      orderData.allocationData.length > 0
    ) {
      if (orderData.allocationData.length === 1) {
        this.mapAllocationData(orderData, orderData.allocationData[0]);
      } else if (orderData.allocationData.length > 1) {
        for (let allocationItem of orderData.allocationData) {
          this.mapAllocationData(orderData, allocationItem);
        }
      }
    } else {
    }
  }

  private mapAllocationData(
    orderData: CubiqTS.OrderDataImpl | BmarkOrderData,
    allocationData: CubiqTS.AllocationDataImpl,
  ): void {
    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      allocationData instanceof CubiqTS.AllocationDataImpl
    ) {
      let allocationIdString = allocationData.allocationId.toString();
      let orderId = this.getItemKey(orderData);
      let allocationMetaDataEntry = new BmarkAllocationMetaData();
      allocationMetaDataEntry.orderId = orderId;
      allocationMetaDataEntry.allocationId = allocationData.allocationId;
      allocationMetaDataEntry.allocationIdString = allocationIdString;
      allocationMetaDataEntry.allocationType = allocationData.allocationType;
      allocationMetaDataEntry.visibleOnItsOwn = this.activeUser.canSeeBmarkHedgeAllocation(
        orderData,
        allocationData,
      );

      // handle bmarkAllocationMetaData
      if (this.bmarkAllocationMetaData.has(allocationIdString)) {
        this.bmarkAllocationMetaData.delete(allocationIdString);
      }
      this.bmarkAllocationMetaData.set(allocationIdString, allocationMetaDataEntry);
      // handle bmarkOrderAllocationMapping
      if (this.bmarkOrderAllocationMapping.has(orderId)) {
        if (!this.bmarkOrderAllocationMapping.get(orderId).has(allocationIdString)) {
          this.bmarkOrderAllocationMapping.get(orderId).add(allocationIdString);
        }
      } else {
        this.bmarkOrderAllocationMapping.set(orderId, new Set([allocationIdString]));
      }
      // if there are component allocations
      if (
        Array.isArray(allocationData.componentAllocations) &&
        allocationData.componentAllocations.length > 0 &&
        allocationData.componentAllocations[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        if (allocationData.componentAllocations.length > 0) {
          for (let allocationItem of allocationData.componentAllocations) {
            this.mapAllocationData(orderData, allocationItem);
          }
        }
      }
    } else {
      logger.out.error(
        `${this.constructor.name}.processAllocationForMetaData() Invalid OrderData or AllocationData`,
      );
    }
  }

  /* ******************************************************* */
  /* PRE-PROCESSING  */

  public preprocessOrderData(orderData: CubiqTS.OrderDataImpl): BmarkOrderData {
    if (
      orderData != null &&
      Array.isArray(orderData.allocationData) &&
      orderData.allocationData.length === 1 &&
      orderData.allocationData[0] instanceof CubiqTS.AllocationDataImpl
    ) {
      let bmarkOrder = new BmarkOrderData(orderData as BmarkOrderData);
      let allocationMetaMap: Map<string, BmarkAllocationMetaData> = new Map<
        string,
        BmarkAllocationMetaData
      >();
      this.preprocessAllocation(orderData.allocationData[0], allocationMetaMap);
      //
      bmarkOrder.traderGroupNames = this.resolveTraderGroupNames(bmarkOrder, allocationMetaMap);
      bmarkOrder.spawned = allocationMetaMap.size > 1;
      bmarkOrder.primaryKey =
        bmarkOrder.orderId instanceof CubiqTS.Long
          ? bmarkOrder.orderId.toString()
          : bmarkOrder.orderId;
      return bmarkOrder;
    } else if (orderData.orderType === CubiqTS.OrderType.BMALGO) {
      let bmarkOrder = new BmarkOrderData(orderData as BmarkOrderData);
      bmarkOrder.traderGroupNames = this.traderGroupLookup.getTraderGroup(
        orderData.deskId,
      ).groupCode;
      bmarkOrder.spawned = false;
      bmarkOrder.primaryKey =
        bmarkOrder.orderId instanceof CubiqTS.Long
          ? bmarkOrder.orderId.toString()
          : bmarkOrder.orderId;
      return bmarkOrder;
    } else if (
      orderData != null &&
      Array.isArray(orderData.allocationData) &&
      orderData.allocationData.length > 1
    ) {
      logger.out.error(
        `${this.constructor.name}.preprocessOrderData() multi-allocation not supported`,
      );
      return null;
    } else {
      logger.out.error(`${this.constructor.name}.preprocessOrderData() no allocations`);
      return null;
    }
  }

  private preprocessAllocation(
    allocationData: CubiqTS.AllocationDataImpl,
    allocationMetaMap: Map<string, BmarkAllocationMetaData>,
  ): void {
    if (allocationData instanceof CubiqTS.AllocationDataImpl) {
      let baseCcy = typeof allocationData.baseCcy === "string" ? allocationData.baseCcy : null;
      let nonBaseCcy =
        typeof allocationData.nonbaseCcy === "string" ? allocationData.nonbaseCcy : null;
      let traderGroupName = null;
      if (allocationData.deskId instanceof CubiqTS.Long) {
        let traderGroup: CubiqTS.TradingGroupImpl = this.traderGroupLookup.getTraderGroup(
          allocationData.deskId,
        );
        traderGroupName =
          traderGroup instanceof CubiqTS.TradingGroupImpl ? traderGroup.groupCode : null;
      }

      if (
        typeof traderGroupName === "string" &&
        typeof baseCcy === "string" &&
        !allocationMetaMap.has(allocationData.allocationId?.toString())
      ) {
        let helperModel: BmarkAllocationMetaData = new BmarkAllocationMetaData();
        helperModel.allocationId = allocationData.allocationId;
        helperModel.allocationIdString =
          allocationData.allocationId instanceof CubiqTS.Long
            ? allocationData.allocationId.toString()
            : "";
        helperModel.baseCcy = baseCcy;
        helperModel.nonBaseCcy = nonBaseCcy;
        helperModel.traderGroupName = traderGroupName;
        helperModel.allocationType = allocationData.allocationType;
        allocationMetaMap.set(allocationData.allocationId?.toString(), helperModel);
      }

      // component allocations
      if (
        Array.isArray(allocationData.componentAllocations) &&
        allocationData.componentAllocations.length > 0 &&
        allocationData.componentAllocations[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        if (allocationData.componentAllocations.length > 0) {
          for (let allocationItem of allocationData.componentAllocations) {
            this.preprocessAllocation(allocationItem, allocationMetaMap);
          }
        }
      }
    }
  }

  public resolveTraderGroupNames(
    orderData: BmarkOrderData,
    allocationMetaMap: Map<string, BmarkAllocationMetaData>,
  ): string {
    let result: string = "";

    if (orderData instanceof CubiqTS.OrderDataImpl && this.isBMType(orderData)) {
      if (
        orderData.allocationData.length === 1 &&
        orderData.allocationData[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        let allocationData: CubiqTS.AllocationDataImpl = orderData.allocationData[0];

        if (
          allocationData.allocationType === CubiqTS.AllocationType.PRIME ||
          allocationMetaMap.size === 1
        ) {
          // CROSS || PRIME
          result =
            allocationMetaMap.get(allocationData.allocationId?.toString()) instanceof
            BmarkAllocationMetaData
              ? allocationMetaMap.get(allocationData.allocationId?.toString()).traderGroupName
              : "-";
        } else if (allocationMetaMap.size > 1) {
          // component allocations
          let baseCcy = orderData.isInvertedOrderFg
            ? allocationData.nonbaseCcy
            : allocationData.baseCcy;
          for (let [key, helperModel] of allocationMetaMap.entries()) {
            if (key != allocationData.allocationId?.toString()) {
              if (helperModel.baseCcy === baseCcy || helperModel.nonBaseCcy === baseCcy) {
                let tg = helperModel.traderGroupName + (result.length > 0 ? "/" : "");
                result = tg + result;
              } else {
                let tg = (result.length > 0 ? "/" : "") + helperModel.traderGroupName;
                result = result + tg;
              }
            }
          }
        }
      }
    }
    return result === "" ? "-" : result;
  }

  /* ******************************************************* */
  /* POST-PROCESSING  */

  /* ******************************************************* */
  /* BMARK VISIBILITY FOR INTERNAL(Local) ORDERS (stored here)  */

  /**
   * returns orderData clone with applied visibility
   */
  public processLocalBmarkOrderDataForVisibility(
    orderData: BmarkOrderData,
    noLog: boolean = false,
  ): BmarkOrderData {
    if (orderData instanceof CubiqTS.OrderDataImpl) {
      try {
        let visibleOrderData: BmarkOrderData = BmarkOrderData.clone(orderData); //orderData.clone();
        let orderId = this.getItemKey(visibleOrderData);
        //
        if (
          this.orderHasAllocations(orderId) &&
          Array.isArray(visibleOrderData.allocationData) &&
          visibleOrderData.allocationData.length > 0
        ) {
          let allocationsMetaData = this.getAllocationMetaDataForOrder(orderId);
          let allocationsDeleteList = allocationsMetaData
            .filter(
              (item) => !this.isLocalAllocationVisibleInContext(item.allocationIdString, noLog),
            )
            .map((item) => item.allocationIdString);
          if (Array.isArray(allocationsDeleteList) && allocationsDeleteList.length > 0) {
            if (visibleOrderData.allocationData.length === 1) {
              if (
                allocationsDeleteList.includes(
                  visibleOrderData.allocationData[0].allocationId.toString(),
                )
              ) {
                visibleOrderData = null;
              } else {
                this.deleteComponentAllocationData(
                  visibleOrderData.allocationData[0],
                  allocationsDeleteList,
                );
              }
            } else if (visibleOrderData.allocationData.length > 1) {
              for (let i = 0; i < visibleOrderData.allocationData.length; i++) {
                if (
                  allocationsDeleteList.includes(
                    visibleOrderData.allocationData[i].allocationId.toString(),
                  )
                ) {
                  visibleOrderData.allocationData.splice(i, 1);
                } else {
                  this.deleteComponentAllocationData(
                    visibleOrderData.allocationData[i],
                    allocationsDeleteList,
                  );
                }
              }
            }
          }
        }
        return visibleOrderData;
      } catch (ex) {
        logger.out.error(
          `${this.constructor.name}.processLocalBmarkOrderDataForVisibility() Exception:`,
          ex,
        );
      }
    }
    return null;
  }

  /* ******************************************************* */
  /* BMARK VISIBILITY FOR EXTERNAL ORDERS (not stored here)  */
  private isBMType(orderData: CubiqTS.OrderDataImpl) {
    return (
      orderData.orderType === CubiqTS.OrderType.BENCHMARK ||
      orderData.orderType === CubiqTS.OrderType.BMALGO
    );
  }

  private isBenchmark(orderData: CubiqTS.OrderDataImpl) {
    return orderData.orderType === CubiqTS.OrderType.BENCHMARK;
  }

  private isBMAlgo(orderData: CubiqTS.OrderDataImpl) {
    return orderData.orderType === CubiqTS.OrderType.BMALGO;
  }

  public processExternalOrderDataForVisibility(
    orderData: Array<CubiqTS.OrderDataImpl>,
  ): Array<CubiqTS.OrderDataImpl> {
    let result: Array<CubiqTS.OrderDataImpl> = new Array<CubiqTS.OrderDataImpl>();
    if (Array.isArray(orderData) && orderData.length > 0) {
      for (let orderDataItem of orderData) {
        try {
          if (orderDataItem instanceof CubiqTS.OrderDataImpl) {
            if (
              (this.isBMType(orderDataItem) &&
                HEDGE_ORDER_REJECT_STATES.has(orderDataItem.orderState)) ||
              this.isBMAlgo(orderDataItem)
            ) {
              if (this.activeUser.canSeeBmarkHedgeOrder(orderDataItem, false)) {
                let incoming: BmarkOrderData = orderDataItem as BmarkOrderData;
                let visibleOrderDataItem: BmarkOrderData = new BmarkOrderData(incoming);
                visibleOrderDataItem = this.preprocessOrderData(visibleOrderDataItem);
                result.push(visibleOrderDataItem);
              }
            } else if (this.isBMType(orderDataItem)) {
              let visibleOrderDataItem: BmarkOrderData = this.preprocessOrderData(orderDataItem);
              if (
                visibleOrderDataItem != null &&
                Array.isArray(visibleOrderDataItem) &&
                visibleOrderDataItem.allocationData.length === 1
              ) {
                if (
                  this.processRootAllocationDataForVisibility(
                    visibleOrderDataItem,
                    visibleOrderDataItem.allocationData[0],
                  )
                ) {
                  // if something is visible
                  result.push(visibleOrderDataItem);
                }
              } else if (
                visibleOrderDataItem != null &&
                Array.isArray(visibleOrderDataItem) &&
                visibleOrderDataItem.allocationData.length > 1
              ) {
                logger.out.info(
                  `${this.constructor.name}.processExternalOrderDataForVisibility() multi-allocation is not supported`,
                );
                let isSomethingVisible: boolean = false;
                for (let allocationItem of visibleOrderDataItem.allocationData) {
                  isSomethingVisible =
                    isSomethingVisible ||
                    this.processRootAllocationDataForVisibility(
                      visibleOrderDataItem,
                      allocationItem,
                    );
                }

                if (isSomethingVisible) {
                  result.push(visibleOrderDataItem);
                }
              } else {
                logger.out.info(
                  `${this.constructor.name}.processExternalOrderDataForVisibility() multi-allocation is not supported`,
                );
              }
            } else {
              // not a benchmark
              if (this.activeUser.canSeeOrder(orderDataItem, false)) {
                let outgoing: CubiqTS.OrderDataImpl = new CubiqTS.OrderDataImpl();
                outgoing.copyFrom(orderDataItem);
                result.push(outgoing);
              }
            }
          }
        } catch (ex) {
          logger.out.error(`ERROR: BMARK: processOrderDataForVisibility(): Exception:`, ex);
        }
      }
    }
    return result;
  }

  /* ******************************************************* */
  /* BMARK VISIBILITY - HELPERS  */

  // allocationData - should be root allocation
  // returns true if any of the allocations is visible, false otherwise
  private processRootAllocationDataForVisibility(
    orderDataItem: CubiqTS.OrderDataImpl,
    allocationData: CubiqTS.AllocationDataImpl,
  ): boolean {
    let allocationsMetaData = new Map<string, BmarkAllocationMetaData>();
    this.createAllocationDataVisibilityMetaData(orderDataItem, allocationData, allocationsMetaData);
    if (allocationsMetaData.size > 0) {
      let allocationsDeleteList = Array.from(allocationsMetaData.values())
        .filter(
          (item) =>
            !this.isExternalAllocationVisibleInContext(
              item.allocationIdString,
              allocationsMetaData,
            ),
        )
        .map((item) => item.allocationIdString);
      if (Array.isArray(allocationsDeleteList) && allocationsDeleteList.length > 0) {
        if (
          orderDataItem.allocationData.length === 1 &&
          orderDataItem.allocationData[0].allocationId === allocationData.allocationId
        ) {
          if (
            allocationsDeleteList.includes(orderDataItem.allocationData[0].allocationId.toString())
          ) {
            return false; // nothing is visible
          } else {
            let shouldRootBeDeleted = this.deleteComponentAllocationData(
              orderDataItem.allocationData[0],
              allocationsDeleteList,
            );
            return !shouldRootBeDeleted; // In theory shouldRootBeDeleted will always be false, because at this point at least the cross is visible
          }
        } else if (orderDataItem.allocationData.length > 1) {
          for (let i = 0; i < orderDataItem.allocationData.length; i++) {
            if (orderDataItem.allocationData[i].allocationId === allocationData.allocationId) {
              // loop until you find the chosen one
              if (
                allocationsDeleteList.includes(
                  orderDataItem.allocationData[i].allocationId.toString(),
                )
              ) {
                orderDataItem.allocationData.splice(i, 1);
                return false; // nothing is visible
              } else {
                this.deleteComponentAllocationData(
                  orderDataItem.allocationData[i],
                  allocationsDeleteList,
                );
                return true; // ta least the cross is visible
              }
            }
          }
        } else if (this.isBMAlgo(orderDataItem)) {
          return true;
        }

        return false; // just in case the chosen one is not found (which it shouldn't happen)
      } else {
        // nothing to delete
        return true;
      }
    } else {
      // something went wrong with building the visibility meta-data
      return false;
    }
  }

  private createAllocationDataVisibilityMetaData(
    orderData: CubiqTS.OrderDataImpl,
    allocationData: CubiqTS.AllocationDataImpl,
    allocationsMetaData: Map<string, BmarkAllocationMetaData>,
  ): void {
    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      allocationData instanceof CubiqTS.AllocationDataImpl
    ) {
      try {
        let allocationId = allocationData.allocationId.toString();
        let orderId = this.getItemKey(orderData);
        let allocationMetaDataEntry = new BmarkAllocationMetaData();
        allocationMetaDataEntry.orderId = orderId;
        allocationMetaDataEntry.allocationIdString = allocationId;
        allocationMetaDataEntry.allocationType = allocationData.allocationType;
        allocationMetaDataEntry.visibleOnItsOwn = this.activeUser.canSeeBmarkHedgeAllocation(
          orderData,
          allocationData,
        );

        if (allocationsMetaData.has(allocationId)) {
          allocationsMetaData.delete(allocationId);
        }
        allocationsMetaData.set(allocationId, allocationMetaDataEntry);

        if (
          Array.isArray(allocationData.componentAllocations) &&
          allocationData.componentAllocations.length > 0 &&
          allocationData.componentAllocations[0] instanceof CubiqTS.AllocationDataImpl
        ) {
          if (allocationData.componentAllocations.length > 0) {
            for (let allocationItem of allocationData.componentAllocations) {
              this.createAllocationDataVisibilityMetaData(
                orderData,
                allocationItem,
                allocationsMetaData,
              );
            }
          }
        }
      } catch (ex) {
        logger.out.error(`ERROR: BMARK: processOrderDataForVisibility(): Exception:`, ex);
      }
    } else {
      logger.out.error(
        `ERROR: BMARK: processAllocationForMetaData(): Invalid OrderData or AllocationData`,
      );
    }
  }

  public isExternalAllocationVisibleInContext(
    allocationId: string,
    allocationsMetaData: Map<string, BmarkAllocationMetaData>,
  ): boolean {
    let result: boolean = false;
    if (allocationsMetaData instanceof Map && allocationsMetaData.has(allocationId)) {
      let metaData: BmarkAllocationMetaData = allocationsMetaData.get(allocationId);
      if (metaData.allocationType === CubiqTS.AllocationType.CROSS) {
        for (let otherMetaDataItem of allocationsMetaData.values()) {
          result =
            result ||
            (typeof otherMetaDataItem.visibleOnItsOwn === "boolean"
              ? otherMetaDataItem.visibleOnItsOwn
              : false);
        }
      } else if (
        metaData.allocationType === CubiqTS.AllocationType.PRIME ||
        metaData.allocationType === CubiqTS.AllocationType.COMPONENT
      ) {
        result = metaData.visibleOnItsOwn;
      }
    }
    return result;
  }

  public isLocalAllocationVisibleInContext(allocationId: string, noLog: boolean = false): boolean {
    let result: boolean = false;
    if (this.allocationHasMetaData(allocationId)) {
      let metaData: BmarkAllocationMetaData = this.getAllocationMetaData(allocationId);
      if (metaData.allocationType === CubiqTS.AllocationType.CROSS) {
        let otherMetaData = this.getAllocationMetaDataForOrder(metaData.orderId);
        for (let otherMetaDataItem of otherMetaData) {
          result =
            result ||
            (typeof otherMetaDataItem.visibleOnItsOwn === "boolean"
              ? otherMetaDataItem.visibleOnItsOwn
              : false);
          if (!noLog) {
            logger.out.info(
              `${this.constructor.name}.isLocalAllocationVisibleInContext allocationId:${allocationId} AllocationType.CROSS visibleOnItsOwn:${otherMetaDataItem.visibleOnItsOwn}`,
            );
          }
        }
      } else if (
        metaData.allocationType === CubiqTS.AllocationType.PRIME ||
        metaData.allocationType === CubiqTS.AllocationType.COMPONENT
      ) {
        result = metaData.visibleOnItsOwn;
        if (!noLog) {
          logger.out.info(
            `${
              this.constructor.name
            }.isLocalAllocationVisibleInContext allocationId:${allocationId} AllocationType.${
              CubiqTS.AllocationType[metaData.allocationType]
            } visibleOnItsOwn:${metaData.visibleOnItsOwn}`,
          );
        }
      }
    } else {
      if (!noLog) {
        logger.out.info(
          `${this.constructor.name}.isLocalAllocationVisibleInContext allocationId:${allocationId} has no metadata`,
        );
      }
    }
    return result;
  }

  private deleteComponentAllocationData(
    allocationData: CubiqTS.AllocationDataImpl,
    allocationsDeleteList: Array<string>,
  ): boolean {
    if (
      allocationData instanceof CubiqTS.AllocationDataImpl &&
      Array.isArray(allocationsDeleteList) &&
      allocationsDeleteList.length > 0
    ) {
      if (allocationsDeleteList.includes(allocationData.allocationId.toString())) {
        return true;
      } else {
        if (
          Array.isArray(allocationData.componentAllocations) &&
          allocationData.componentAllocations.length > 0
        ) {
          for (let i = 0; i < allocationData.componentAllocations.length; i++) {
            let shouldBeDeleted = this.deleteComponentAllocationData(
              allocationData.componentAllocations[i],
              allocationsDeleteList,
            );
            if (shouldBeDeleted) {
              let deletedComponent = allocationData.componentAllocations.splice(i, 1);
              logger.out.info(
                `${this.constructor.name}.deleteComponentAllocationData() deleted component allocation ${deletedComponent[0].allocationId}`,
              );
            }
          }
        }
      }
    }
    return false;
  }

  /* ******************************************************* */
  /* ALLOCATION Helpers  */

  public areSetsEqual(as: Set<string>, bs: Set<string>) {
    if (as.size !== bs.size) return false;
    for (let a of as) {
      if (!bs.has(a)) {
        return false;
      }
    }
    return true;
  }

  public getAllAllocationIdsForOrder(
    orderData: CubiqTS.OrderDataImpl | BmarkOrderData,
  ): Set<string> {
    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      Array.isArray(orderData.allocationData) &&
      orderData.allocationData.length > 0
    ) {
      let allocationIds = new Set<string>();
      if (orderData.allocationData.length === 1) {
        this.getAllocationIds(orderData.allocationData[0], allocationIds);
      } else if (orderData.allocationData.length > 1) {
        for (let allocationItem of orderData.allocationData) {
          this.getAllocationIds(allocationItem, allocationIds);
        }
      }
      return allocationIds;
    }
    return null;
  }

  private getAllocationIds(
    allocationData: CubiqTS.AllocationDataImpl,
    allocationIds: Set<string>,
  ): void {
    if (allocationData instanceof CubiqTS.AllocationDataImpl) {
      // root allocation
      allocationIds.add(allocationData.allocationId.toString());
      // children allocations
      if (
        Array.isArray(allocationData.componentAllocations) &&
        allocationData.componentAllocations.length > 0 &&
        allocationData.componentAllocations[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        if (allocationData.componentAllocations.length > 0) {
          for (let allocationItem of allocationData.componentAllocations) {
            this.getAllocationIds(allocationItem, allocationIds);
          }
        }
      }
    }
  }

  public getAllAllocationDatasetForOrder(
    orderData: CubiqTS.OrderDataImpl | BmarkOrderData,
  ): Set<CubiqTS.AllocationData> {
    let allocationDataset = new Set<CubiqTS.AllocationData>();
    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      Array.isArray(orderData.allocationData) &&
      orderData.allocationData.length > 0
    ) {
      if (orderData.allocationData.length === 1) {
        this.getAllocationDataset(orderData.allocationData[0], allocationDataset);
      } else if (orderData.allocationData.length > 1) {
        for (let allocationItem of orderData.allocationData) {
          this.getAllocationDataset(allocationItem, allocationDataset);
        }
      }
    }
    return allocationDataset;
  }

  private getAllocationDataset(
    allocationData: CubiqTS.AllocationDataImpl,
    allocationDataset: Set<CubiqTS.AllocationData>,
  ): void {
    if (allocationData instanceof CubiqTS.AllocationDataImpl) {
      // root allocation
      allocationDataset.add(allocationData);
      // children allocations
      if (
        Array.isArray(allocationData.componentAllocations) &&
        allocationData.componentAllocations.length > 0 &&
        allocationData.componentAllocations[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        if (allocationData.componentAllocations.length > 0) {
          for (let allocationItem of allocationData.componentAllocations) {
            this.getAllocationDataset(allocationItem, allocationDataset);
          }
        }
      }
    }
  }

  public findAllocationDataForOrder(
    orderData: CubiqTS.OrderDataImpl | BmarkOrderData,
    allocationId: string,
  ): CubiqTS.AllocationDataImpl {
    if (
      typeof allocationId === "string" &&
      orderData instanceof CubiqTS.OrderDataImpl &&
      Array.isArray(orderData.allocationData) &&
      orderData.allocationData.length > 0
    ) {
      let result;
      if (orderData.allocationData.length === 1) {
        result = this.findAllocationData(orderData.allocationData[0], allocationId);
      } else if (orderData.allocationData.length > 1) {
        for (let allocationItem of orderData.allocationData) {
          result = this.findAllocationData(allocationItem, allocationId);
        }
      }
      return result;
    } else {
      return null;
    }
  }

  private findAllocationData(
    allocationData: CubiqTS.AllocationDataImpl,
    allocationId: string,
  ): CubiqTS.AllocationDataImpl {
    if (typeof allocationId === "string" && allocationData instanceof CubiqTS.AllocationDataImpl) {
      let result = null;
      result = allocationData.allocationId.toString() === allocationId ? allocationData : null;

      if (
        result != null &&
        Array.isArray(allocationData.componentAllocations) &&
        allocationData.componentAllocations.length > 0 &&
        allocationData.componentAllocations[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        if (allocationData.componentAllocations.length > 0) {
          for (let allocationItem of allocationData.componentAllocations) {
            if (result != null) {
              result = this.findAllocationData(allocationItem, allocationId);
            }
          }
        }
      }
      return result;
    } else {
      return null;
    }
  }

  /* ******************************************************* */
  /* FIXINGS - ACTIVE  */

  public getAllActiveFixings(): Array<BmarkFixingData> {
    return Array.from(this.activeFixings.values());
  }

  public getAllActiveFixingsIds(): Array<string> {
    return Array.from(this.activeFixings.keys());
  }

  public getActiveFixingData(fixingId: string): BmarkFixingData {
    return typeof fixingId === "string" && this.activeFixings.has(fixingId)
      ? this.activeFixings.get(fixingId)
      : null;
  }

  public hasActiveFixing(fixingId: string): boolean {
    return this.getActiveFixingData(fixingId) != null;
  }

  public addActiveFixing(fixingData: BmarkFixingData): void {
    if (fixingData instanceof BmarkFixingData && typeof fixingData.id === "string") {
      this.activeFixings.set(fixingData.id, fixingData);
    }
  }

  public hasOrderIdInActiveFixing(fixingId: string, orderId: string): boolean {
    if (
      typeof fixingId === "string" &&
      this.activeFixings.has(fixingId) &&
      typeof orderId === "string"
    ) {
      return this.activeFixings.get(fixingId).hasHedgeOrderId(orderId);
    }
    return false;
  }

  public hasOrderIdInRecentFixing(fixingId: string, orderId: string): boolean {
    if (
      typeof fixingId === "string" &&
      this.recentFixings.has(fixingId) &&
      typeof orderId === "string"
    ) {
      return this.recentFixings.get(fixingId).hasHedgeOrderId(orderId);
    }
    return false;
  }

  public addOrderToActiveFixing(
    fixingId: string,
    orderId: string,
    orderData: CubiqTS.OrderData,
  ): void {
    if (
      typeof fixingId === "string" &&
      this.activeFixings.has(fixingId) &&
      typeof orderId === "string"
    ) {
      this.activeFixings
        .get(fixingId)
        .addHedgeOrder(orderData, this.traderGroupLookup.getTraderGroup(orderData.deskId));
    }
  }

  public updateOrderInActiveFixing(
    fixingId: string,
    orderId: string,
    orderData: CubiqTS.OrderData,
  ): void {
    if (
      typeof fixingId === "string" &&
      this.activeFixings.has(fixingId) &&
      typeof orderId === "string"
    ) {
      let fixing = this.activeFixings.get(fixingId);
      fixing.updateHedgeOrder(orderData, this.traderGroupLookup.getTraderGroup(orderData.deskId));
      this.activeFixings.set(fixingId, fixing);
    }
  }

  public deleteOrderIdsInActiveFixing(fixingId: string, orderIds: Array<string>): void {
    if (orderIds instanceof Array && orderIds.length > 0) {
      for (let orderId of orderIds) {
        this.deleteOrderIdInActiveFixing(fixingId, orderId);
      }
    }
  }

  public deleteOrderIdInActiveFixing(fixingId: string, orderId: string): void {
    if (
      typeof fixingId === "string" &&
      this.activeFixings.has(fixingId) &&
      typeof orderId === "string"
    ) {
      this.activeFixings.get(fixingId).removeHedgeOrder(orderId);
    } else {
      throw new Error(`Invalid fixingId (${fixingId}) or orderId (${orderId})`);
    }
  }

  public updateActiveFixingData(fixingData: BmarkFixingData): void {
    if (
      fixingData instanceof BmarkFixingData &&
      typeof fixingData.id === "string" &&
      this.activeFixings.has(fixingData.id)
    ) {
      this.activeFixings.set(fixingData.id, fixingData);
    }
  }

  public deleteActiveFixing(fixingId: string): void {
    if (typeof fixingId === "string" && this.activeFixings.has(fixingId)) {
      this.activeFixings.delete(fixingId);
      logger.out.info(`${this.constructor.name}.deleteActiveFixing() fixingId:${fixingId}`);
    }
  }

  /* ******************************************************* */
  /* FIXINGS - RECENT  */

  public hasRecentFixing(fixingId: string): boolean {
    return this.getRecentFixingData(fixingId) != null;
  }

  public getAllRecentFixings(): Array<BmarkFixingData> {
    return Array.from(this.recentFixings.values());
  }

  public getAllRecentFixingsIds(): Array<string> {
    return Array.from(this.recentFixings.keys());
  }

  public getRecentFixingData(fixingId: string): BmarkFixingData {
    return typeof fixingId === "string" && this.recentFixings.has(fixingId)
      ? this.recentFixings.get(fixingId)
      : null;
  }

  public addRecentFixing(fixingData: BmarkFixingData): void {
    if (fixingData instanceof BmarkFixingData && typeof fixingData.id === "string") {
      this.recentFixings.set(fixingData.id, fixingData);
    }
  }

  /**
   * Instead of adding, set the given order IDs for the fixing
   * @param fixingId the fixing id to change the order IDs for
   * @param orderIds the new order ID list
   * @returns a list of order IDs that were set for the fixing but no longer are
   */
  public setOrderIdsToRecentFixing(
    fixingId: string,
    orders: Array<CubiqTS.OrderData>,
  ): Array<string> {
    if (typeof fixingId === "string" && this.recentFixings.has(fixingId)) {
      let hedgeOrders = orders.map((orderData) => {
        return {
          orderData: orderData,
          traderGroup: this.traderGroupLookup.getTraderGroup(orderData.deskId),
        };
      });
      return this.recentFixings.get(fixingId).setHedgeOrders(hedgeOrders);
    }

    return [];
  }

  public addOrderToRecentFixing(
    fixingId: string,
    orderId: string,
    orderData: CubiqTS.OrderData,
  ): void {
    if (
      typeof fixingId === "string" &&
      this.recentFixings.has(fixingId) &&
      typeof orderId === "string"
    ) {
      this.recentFixings
        .get(fixingId)
        .addHedgeOrder(orderData, this.traderGroupLookup.getTraderGroup(orderData.deskId));
    } else {
      throw new Error(`Invalid fixingId (${fixingId}) or orderId (${orderId})`);
    }
  }

  public updateOrderInRecentFixing(
    fixingId: string,
    orderId: string,
    orderData: CubiqTS.OrderData,
  ): void {
    if (
      typeof fixingId === "string" &&
      this.recentFixings.has(fixingId) &&
      typeof orderId === "string"
    ) {
      let fixing = this.recentFixings.get(fixingId);
      fixing.updateHedgeOrder(orderData, this.traderGroupLookup.getTraderGroup(orderData.deskId));
      this.recentFixings.set(fixingId, fixing);
    }
  }

  public deleteOrderIdsInRecentFixing(fixingId: string, orderIds: Array<string>): void {
    if (orderIds instanceof Array && orderIds.length > 0) {
      for (let orderId of orderIds) {
        this.deleteOrderIdInRecentFixing(fixingId, orderId);
      }
    }
  }

  public deleteOrderIdInRecentFixing(fixingId: string, orderId: string): void {
    if (
      typeof fixingId === "string" &&
      this.recentFixings.has(fixingId) &&
      typeof orderId === "string"
    ) {
      this.recentFixings.get(fixingId).removeHedgeOrder(orderId);
    } else {
      throw new Error(`Invalid fixingId (${fixingId}) or orderId (${orderId})`);
    }
  }

  public clearOrderIdsInRecentFixing(fixingId: string): void {
    if (typeof fixingId === "string" && this.recentFixings.has(fixingId)) {
      this.recentFixings.get(fixingId).clearHedgeOrders();
    } else {
      throw new Error(`Invalid fixingId (${fixingId})`);
    }
  }

  public updateRecentFixing(fixingData: BmarkFixingData): void {
    if (
      fixingData instanceof BmarkFixingData &&
      typeof fixingData.id === "string" &&
      this.recentFixings.has(fixingData.id)
    ) {
      this.recentFixings.set(fixingData.id, fixingData);
    }
  }

  public deleteRecentFixing(fixingId: string): void {
    if (typeof fixingId === "string" && this.recentFixings.has(fixingId)) {
      let fixingData = this.recentFixings.get(fixingId);
      this.recentFixings.delete(fixingId);
      BmarkFixingData.destroy(fixingData);
      logger.out.info(`${this.constructor.name}.deleteRecentFixing() fixingId:${fixingId}`);
    }
  }

  /* ******************************************************* */
  /* FIXINGS - ALL  */

  public hasFixingId(fixingId: string): boolean {
    return this.hasActiveFixing(fixingId) || this.hasRecentFixing(fixingId);
  }

  public getFixingData(id: string): BmarkFixingData | null {
    return this.hasActiveFixing(id)
      ? this.getActiveFixingData(id)
      : this.hasRecentFixing(id)
      ? this.getRecentFixingData(id)
      : null;
  }

  public moveFixingTo(
    fixingId: string,
    targetType: BmarkFixingType,
    orderIds: string[] = [],
  ): void {
    if (typeof fixingId === "string" && targetType in BmarkFixingType) {
      switch (targetType) {
        case BmarkFixingType.ACTIVE:
          if (this.hasRecentFixing(fixingId)) {
            let fixingData: BmarkFixingData = this.getRecentFixingData(fixingId);
            fixingData.type = BmarkFixingType.ACTIVE;
            this.addActiveFixing(fixingData);
            this.deleteRecentFixing(fixingId);
            logger.out.info(
              `${this.constructor.name}.moveFixingTo() Fixing ${fixingId} moved to ACTIVE`,
            );
          } else {
            logger.out.error(
              `${this.constructor.name}.moveFixingTo(): Moving fixing ${fixingId} not found in RECENT`,
            );
          }
          break;
        case BmarkFixingType.RECENT:
          if (this.hasActiveFixing(fixingId)) {
            let fixingData: BmarkFixingData = this.getActiveFixingData(fixingId);
            // fixing might already be set to BmarkFixingType.RECENT
            // this is a consequence of allocator and is expected
            fixingData.type = BmarkFixingType.RECENT;
            this.addRecentFixing(fixingData);
            this.deleteActiveFixing(fixingId);
            logger.out.info(
              `${this.constructor.name}.moveFixingTo() Fixing ${fixingId} moved to RECENT`,
            );
          } else {
            logger.out.error(
              `${this.constructor.name}.moveFixingTo() Moving fixing ${fixingId} not found in ACTIVE`,
            );
          }
          break;
        default:
          logger.out.error(`${this.constructor.name}.moveFixingTo() Invalid target`);
          break;
      }
    } else {
      logger.out.error(`${this.constructor.name}.moveFixingTo() Invalid input arguments`);
    }
  }

  public sortFixingMap(listType?: BmarkFixingType): void {
    if (listType == null || listType === BmarkFixingType.ACTIVE) {
      this.activeFixings = new Map(
        [...this.activeFixings].sort((a, b) =>
          this.performSort(a[1], b[1], BmarkFixingType.ACTIVE),
        ),
      );
    }

    if (listType == null || listType === BmarkFixingType.RECENT) {
      this.recentFixings = new Map(
        [...this.recentFixings].sort((a, b) =>
          this.performSort(a[1], b[1], BmarkFixingType.RECENT),
        ),
      );
    }
  }

  private performSort(a: BmarkFixingData, b: BmarkFixingData, type: BmarkFixingType): number {
    try {
      let aHard = a.state === HARD_CUTOFF;
      let bHard = b.state === HARD_CUTOFF;
      let aSoft = a.state === SOFT_CUTOFF;
      let bSoft = b.state === SOFT_CUTOFF;
      let aDateTime = a.fixingTimestamp;
      let bDateTime = b.fixingTimestamp;
      if (type === BmarkFixingType.ACTIVE) {
        if ((aHard || bHard) && !(aHard && bHard)) {
          // xor hard
          return aHard ? -1 : 1;
        } else if ((aSoft || bSoft) && !(aSoft && bSoft)) {
          // xor soft
          return aSoft ? -1 : 1;
        } else {
          if (aDateTime === bDateTime) {
            return a.code.localeCompare(b.code);
          }
          return bDateTime > aDateTime ? -1 : 1;
        }
      } else {
        if (aDateTime === bDateTime) {
          return a.code.localeCompare(b.code);
        }
        return aDateTime > bDateTime ? -1 : 1;
      }
    } catch (ex) {
      logger.out.error(`ERROR: Bmark-Fixing-List sorting exception: ${ex}`);
      return 0;
    }
  }

  /* ******************************************************* */
  /* BENCHMARKS - ORDER DATA */

  public updateOrderDataState(
    orderIds: Array<string | CubiqTS.Long>,
    newOrderState: CubiqTS.OrderState,
  ): void {
    if (Array.isArray(orderIds) && orderIds.length > 0 && newOrderState in CubiqTS.OrderState) {
      for (let orderId of orderIds) {
        let castOrderId: string =
          orderId instanceof CubiqTS.Long
            ? orderId.toString()
            : typeof orderId === "string"
            ? orderId
            : null;
        if (typeof castOrderId === "string") {
          let orderData = this.getItem(castOrderId);
          if (orderData instanceof CubiqTS.OrderDataImpl) {
            orderData.orderState = newOrderState;
          }
        }
      }
    }
  }

  public getBmarkOrderDataForFixingId(
    fixingId: string,
    fixingType: BmarkFixingType,
  ): Array<BmarkOrderData> {
    if (typeof fixingId === "string" && fixingType in BmarkFixingType) {
      let orderIds = null;
      switch (fixingType) {
        case BmarkFixingType.ACTIVE:
          if (this.activeFixings.has(fixingId)) {
            orderIds = this.activeFixings.get(fixingId).getHedgeOrderIds();
          }
          break;
        case BmarkFixingType.RECENT:
          if (this.recentFixings.has(fixingId)) {
            orderIds = this.recentFixings.get(fixingId).getHedgeOrderIds();
          }
          break;
      }
      // get the orders
      if (Array.isArray(orderIds) && orderIds.length > 0) {
        return this.getBmarkOrderDataForOrderIds(orderIds, fixingType === BmarkFixingType.ACTIVE);
      }
    }
    return [];
  }

  public getBmarkOrderDataForOrderIds(
    orderIds: Array<string>,
    applyAllocationVisibility: boolean = true,
  ): Array<BmarkOrderData> {
    let orders: Array<BmarkOrderData> = [];
    for (let orderId of orderIds) {
      let orderData = this.getItem(orderId) as BmarkOrderData;
      if (orderData != null) {
        orders.push(
          applyAllocationVisibility
            ? this.processLocalBmarkOrderDataForVisibility(orderData)
            : BmarkOrderData.clone(orderData),
        ); //orderData.clone());
      } else {
        logger.out.error(
          `${this.constructor.name}.getBmarkOrderDataForOrderIds() Benchmark Order Data for ${orderId} not found.`,
        );
      }
    }
    return orders;
  }

  public getBmarkOrderDataForOrderId(
    orderId: string,
    applyAllocationVisibility: boolean = true,
    noLog: boolean = false,
  ): BmarkOrderData | null {
    if (this.hasItem(orderId)) {
      let orderData = this.getItem(orderId) as BmarkOrderData;
      if (orderData) {
        orderData = applyAllocationVisibility
          ? this.processLocalBmarkOrderDataForVisibility(orderData, noLog)
          : BmarkOrderData.clone(orderData); //orderData.clone();
      }
      return orderData;
    }
    return null;
  }

  /* ******************************************************* */
  /* HELPERS */
  /* 
		public cloneBmarkOrder(orderData: BmarkOrderData): BmarkOrderData {
			let cloneOrderData = this._serializer.deserialize(this._serializer.serialize(orderData, this.VALUE), this.VALUE);
			cloneOrderData.traderGroupNames = orderData.traderGroupNames;
			cloneOrderData.spawned = orderData.spawned;
			return cloneOrderData;
		} */

  public cloneOrderImpl(orderData: CubiqTS.OrderDataImpl): CubiqTS.OrderDataImpl {
    return this._serializer.deserialize(
      this._serializer.serialize(orderData, this.VALUE),
      this.VALUE,
    );
  }
}
